// apps/share-web/src/song-texts-backfill-api.ts
//
// Разовый перенос слов владельца: `POST /api/song-texts/backfill`
// (apps/share-worker/functions/api/song-texts/backfill.ts) — без тела,
// self-only (маршрут читает ТОЛЬКО живые текстовые ссылки вызывающего),
// идемпотентный: вызвать раз, вызвать сто раз — результат сходится.
//
// Тот же cookie-контур, что и `fetchLibrary` (`library.ts`) и `fetchSongText`
// (`song-text-api.ts`) — отсюда `credentials: 'include'`: без cookie маршрут
// всегда отвечает 401.
//
// Форма успешного тела — дословно из doc-комментария маршрута
// (`onRequestPost`): `{ ok: true, linksReviewed, created, updated, skipped,
// skippedByReason }`. `created`/`updated` — счётчик песен, которые ПОЛУЧИЛИ
// слова этим прогоном (новая запись против перезаписанной существующей);
// `skippedByReason` — разбивка причин, почему остальные пропущены (у UI это
// единственный источник, чем объяснить «перенесено 0 песен», не молчать и не
// говорить «готово» — см. describeZeroImportReason в ui/library-view.ts).
//
// Любой другой статус (403 деактивированного аккаунта включительно), брошенное
// исключение или неразборное тело — `network`: как и у соседних клиентов
// этого файла, это не про то, ЧТО случилось, а про то, что стоит попробовать
// ещё раз. 401 — единственный статус с собственной причиной (`auth`), потому
// что это единственный сценарий, который UI обязан отличать (незалогинен —
// не «повтори», а «войди»).

export interface SongTextsBackfillReport {
  linksReviewed: number;
  created: number;
  updated: number;
  skipped: number;
  skippedByReason: Record<string, number>;
}

export type SongTextsBackfillResult =
  | { ok: true; report: SongTextsBackfillReport }
  | { ok: false; reason: 'auth' | 'network' };

export async function backfillSongTexts(
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<SongTextsBackfillResult> {
  try {
    const res = await fetchImpl(`${apiBase}/api/song-texts/backfill`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.status === 401) return { ok: false, reason: 'auth' };
    if (!res.ok) return { ok: false, reason: 'network' };
    const body = await res.json() as { ok?: unknown } & Partial<SongTextsBackfillReport>;
    if (!body || body.ok !== true) return { ok: false, reason: 'network' };
    return {
      ok: true,
      report: {
        linksReviewed: body.linksReviewed ?? 0,
        created: body.created ?? 0,
        updated: body.updated ?? 0,
        skipped: body.skipped ?? 0,
        skippedByReason: body.skippedByReason ?? {},
      },
    };
  } catch {
    return { ok: false, reason: 'network' };
  }
}
